/** @jsxImportSource @emotion/react */
import { FC, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { Buttons, Spinner } from '@zip/business-components';
import { Constants } from 'global';
import { useMerchantApplicationContext } from 'contexts/MerchantApplication.context';
import { useSignUpFunctions } from 'utils';
import { HandWithPhoneApp } from 'public/illustrations';
import { PartnerIntegrationModule } from './PartnerIntegrationModule';
import * as styles from './EligibilityCheckModule.styles';

type Case<K extends string> = { kind: K };
type CaseWithValue<K extends string, T> = { kind: K; value: T };

type EligibilityResult = 'partnerIntegration' | 'eligible' | 'notEligible';

type EligibilityCheckStatus =
  | Case<'checking'>
  | CaseWithValue<'hasResult', EligibilityResult>;

// ─── Loading / Eligible view (shared layout, different message) ───────────────

function StatusView({ message }: { message: string }) {
  return (
    <Box css={styles.statusContainer}>
      <Box css={styles.statusContent}>
        <Box css={styles.pocketWrapper}>
          <Spinner />
        </Box>
        <p className="body2" css={styles.statusMessage}>
          {message}
        </p>
      </Box>
    </Box>
  );
}

// ─── Not-eligible view ────────────────────────────────────────────────────────

function NotEligibleView() {
  return (
    <Box css={styles.container}>
      <Box css={styles.notEligibleContent}>
        <Box css={styles.notEligibleCard}>
          <img src={HandWithPhoneApp} alt="" width={96} height={96} />

          <Box css={styles.notEligibleTextGroup}>
            <p className="heading1" css={styles.notEligibleTitle}>
              Sorry we can't take your application at this time
            </p>
            <p className="body2" css={styles.notEligibleBody}>
              When assessing the suitability of a merchant we consider a range
              of factors. We thank you for your consideration and we wish you
              all the best.
            </p>
          </Box>

          <Box css={styles.notEligibleButtonWrapper}>
            <Buttons.Primary
              className="body1"
              css={styles.primaryButton}
              variant="contained"
              onClick={() => {
                window.location.href = Constants.zipBusinessPaymentsUrl;
              }}
            >
              Back to homepage
            </Buttons.Primary>
          </Box>

          <p className="body2" css={styles.contactText}>
            Still want to be a Zip merchant in the future?{' '}
            <a
              href={Constants.merchantHelpCenterBaseUrl}
              className="body1"
              css={styles.contactLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              Contact us here
            </a>
          </p>
        </Box>
      </Box>
    </Box>
  );
}

// ─── Constants ────────────────────────────────────────────────────────────────

const MIN_LOADING_MS = 2000;
const ELIGIBLE_AUTO_ADVANCE_MS = 1000;

// ─── Main module ──────────────────────────────────────────────────────────────

export const EligibilityCheckModule: FC = () => {
  const [status, setStatus] = useState<EligibilityCheckStatus>({
    kind: 'checking',
  });
  const { merchantApplicationId, stepState, nextSignUpStep } =
    useMerchantApplicationContext();
  const hasAutoAdvancedRef = useRef(false);
  const { checkEligibilityAsync } = useSignUpFunctions();

  if (!merchantApplicationId) {
    throw new Error('EligibilityCheckModule requires a merchantApplicationId');
  }

  const needPartnerIntegration = (): boolean => {
    var annualSales = stepState.stepData.merchantApplicationDetails.annualSales;
    var paymentProvider =
      stepState.stepData.merchantApplicationDetails.paymentProvider;
    return (
      paymentProvider?.provider === 'Stripe' &&
      annualSales?.salesRange === 'Under $1M'
    );
  };

  useEffect(() => {
    let advanceTimer: ReturnType<typeof setTimeout> | undefined;

    const run = async () => {
      // Start the minimum loading delay immediately so it runs in parallel
      const minTimer = new Promise<void>((resolve) =>
        setTimeout(resolve, MIN_LOADING_MS)
      );

      let result: EligibilityCheckStatus;

      if (needPartnerIntegration()) {
        result = { kind: 'hasResult', value: 'partnerIntegration' };
      } else {
        try {
          const response = await checkEligibilityAsync(merchantApplicationId);
          result = {
            kind: 'hasResult',
            value: response.eligible ? 'eligible' : 'notEligible',
          };
        } catch {
          result = { kind: 'hasResult', value: 'notEligible' };
        }
      }

      await minTimer; // ensure the minimum loading time has elapsed
      setStatus(result);

      if (result.kind === 'hasResult' && result.value === 'eligible') {
        advanceTimer = setTimeout(() => {
          if (hasAutoAdvancedRef.current) {
            return;
          }

          hasAutoAdvancedRef.current = true;
          nextSignUpStep();
        }, ELIGIBLE_AUTO_ADVANCE_MS);
      }
    };

    run();

    return () => clearTimeout(advanceTimer);
  }, []);

  if (status.kind === 'hasResult' && status.value === 'partnerIntegration') {
    return <PartnerIntegrationModule />;
  }

  if (status.kind === 'hasResult' && status.value === 'eligible') {
    return <StatusView message="Great, you are eligible!" />;
  }

  if (status.kind === 'hasResult' && status.value === 'notEligible') {
    return <NotEligibleView />;
  }

  return (
    <StatusView message="Hold tight... We are checking to see if you are eligible for Zip." />
  );
};

export default EligibilityCheckModule;
